import {
  Card,
  CardContent,
  Typography,
  Grid,
  Stack,
} from '@mui/material';
import { Unit, Lease } from '@urbanmix-tech/shared-js';
import { analyzeTrends, TrendsData } from './trendsUtils';
import { getLongNumberStr } from '@/helpers/common';

interface TrendsSummaryProps {
  allLeases: Lease[];
  allUnits: Map<string, Unit>;
}

export function TrendsSummary({ allLeases, allUnits }: TrendsSummaryProps) {
  const trends = analyzeTrends(allLeases, allUnits);

  if (!trends) {
    return (
      <div className="trends-section border border-zinc-600 rounded-md p-6">
        <Typography variant="h6" className="mb-4">
          Recent Leases
        </Typography>
        <Typography variant="body2" color="textSecondary" className="text-center py-8">
          Coming soon.
        </Typography>
      </div>
    );
  }

  return (
    <div className="trends-section border border-zinc-600 rounded-md p-6">
      <Typography variant="h6" className="mb-6">
        Recent Leases
      </Typography>

      <Grid container spacing={3}>
        {/* Rent Trends */}
        <Grid item xs={12} sm={6}>
          <Card className="h-full">
            <CardContent>
              <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                Monthly Rent
              </Typography>
              <Stack spacing={1}>
                <div>
                  <Typography variant="body2" color="textSecondary">
                    Recent (Last 3 mo)
                  </Typography>
                  <Typography variant="h6">
                    ${getLongNumberStr(trends.recentAvgRent)}
                  </Typography>
                </div>
                <div>
                  <Typography variant="body2" color="textSecondary">
                    Last Year (Same Period)
                  </Typography>
                  <Typography variant="h6">
                    ${getLongNumberStr(trends.historicalAvgRent)}
                  </Typography>
                </div>
                <div className="pt-2 border-t border-zinc-200 dark:border-zinc-700">
                  <Typography variant="body2" color="textSecondary">
                    Change
                  </Typography>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Typography
                      variant="h6"
                      className={trends.rentChange >= 0 ? 'text-green-600' : 'text-red-600'}
                    >
                      ${getLongNumberStr(trends.rentChange)}
                    </Typography>
                    <Typography
                      variant="body2"
                      className={trends.rentChangePercent >= 0 ? 'text-green-600' : 'text-red-600'}
                    >
                      {trends.rentChangePercent >= 0 ? '+' : ''}{trends.rentChangePercent.toFixed(2)}%
                    </Typography>
                  </Stack>
                </div>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* PPSF Trends */}
        <Grid item xs={12} sm={6}>
          <Card className="h-full">
            <CardContent>
              <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                Annual Rent per Sqft
              </Typography>
              <Stack spacing={1}>
                <div>
                  <Typography variant="body2" color="textSecondary">
                    Recent (Last 3 mo)
                  </Typography>
                  <Typography variant="h6">
                    ${trends.recentAvgPPSF.toFixed(2)}
                  </Typography>
                </div>
                <div>
                  <Typography variant="body2" color="textSecondary">
                    Last Year (Same Period)
                  </Typography>
                  <Typography variant="h6">
                    ${trends.historicalAvgPPSF.toFixed(2)}
                  </Typography>
                </div>
                <div className="pt-2 border-t border-zinc-200 dark:border-zinc-700">
                  <Typography variant="body2" color="textSecondary">
                    Change
                  </Typography>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Typography
                      variant="h6"
                      className={trends.ppsfChange >= 0 ? 'text-green-600' : 'text-red-600'}
                    >
                      ${trends.ppsfChange.toFixed(2)}
                    </Typography>
                    <Typography
                      variant="body2"
                      className={trends.ppsfChangePercent >= 0 ? 'text-green-600' : 'text-red-600'}
                    >
                      {trends.ppsfChangePercent >= 0 ? '+' : ''}{trends.ppsfChangePercent.toFixed(2)}%
                    </Typography>
                  </Stack>
                </div>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Lease Count */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="textSecondary">
                New Leases Signed (Last 3 Months)
              </Typography>
              <Typography variant="h6">{trends.recentLeaseCount} leases</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Summary Text */}
      <Stack spacing={2} className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/10 rounded-md">
        <Typography variant="body2">
          <strong>Summary:</strong> In the last 3 months, average rents have{' '}
          <span className={trends.rentChange >= 0 ? 'text-green-600 font-bold' : 'text-red-600 font-bold'}>
            {trends.rentChangePercent >= 0 ? 'increased' : 'decreased'} by {Math.abs(trends.rentChangePercent).toFixed(2)}%
          </span>{' '}
          compared to the same period last year.
        </Typography>
        <Typography variant="body2">
          Annual rent per sqft{' '}
          <span className={trends.ppsfChange >= 0 ? 'text-green-600 font-bold' : 'text-red-600 font-bold'}>
            {trends.ppsfChangePercent >= 0 ? 'increased' : 'decreased'} by {Math.abs(trends.ppsfChangePercent).toFixed(2)}%
          </span>
          , from ${trends.historicalAvgPPSF.toFixed(2)} to ${trends.recentAvgPPSF.toFixed(2)}.
        </Typography>
      </Stack>
    </div>
  );
}
